import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Car, Home, Flame, Droplets, CloudRain, Shield, HeartPulse, Plane, HelpCircle, CheckCircle2, ChevronRight, ArrowLeft, Mic, ClipboardList, Camera, MessageCircle, Ban, Copy } from "lucide-react";
import { AIText } from "@/components/AIText";

const INCIDENTS = [
  { key: "auto accident", tid: "Auto accident", labelKey: "inc_auto", icon: Car },
  { key: "property damage", tid: "Property damage", labelKey: "inc_property", icon: Home },
  { key: "theft", tid: "Theft", labelKey: "inc_theft", icon: Shield },
  { key: "fire", tid: "Fire", labelKey: "inc_fire", icon: Flame },
  { key: "water damage", tid: "Water damage", labelKey: "inc_water", icon: Droplets },
  { key: "property damage", tid: "Weather event", labelKey: "inc_weather", icon: CloudRain },
  { key: "medical billing issue", tid: "Medical billing issue", labelKey: "inc_medical", icon: HeartPulse },
  { key: "property damage", tid: "Travel incident", labelKey: "inc_travel", icon: Plane },
  { key: "other", tid: "Other", labelKey: "inc_other", icon: HelpCircle },
];

export default function Claims() {
  const { t, lang } = useI18n();
  const [claims, setClaims] = useState([]);
  const [active, setActive] = useState(null);

  const load = () => api.get("/claims").then((r) => setClaims(r.data));
  useEffect(() => { load(); }, []);

  const start = async (incident) => {
    try {
      const res = await api.post("/claims", { incident_type: incident });
      setActive(res.data); load();
    } catch { toast.error("Could not start claim"); }
  };

  const toggle = async (idx, done) => {
    const res = await api.put(`/claims/${active.id}/checklist`, { index: idx, done });
    setActive({ ...active, checklist: res.data.checklist });
  };

  if (active) return <ClaimDetail claim={active} onBack={() => { setActive(null); load(); }} toggle={toggle} lang={lang} t={t} />;

  return (
    <AppLayout>
      <h1 className="font-display text-3xl font-semibold text-navy flex items-center gap-2"><ClipboardList className="h-7 w-7 text-brandblue" /> {t("something_happened")}</h1>
      <p className="mt-1 text-slate-500">{t("claims_sub")}</p>

      <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-4">
        {INCIDENTS.map((it, i) => (
          <button key={i} data-testid={`incident-${it.tid}`} onClick={() => start(it.key)}
            className="flex flex-col items-center gap-3 rounded-2xl bg-white border border-slate-100 card-shadow p-6 hover:-translate-y-1 transition-transform">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl brand-gradient text-white"><it.icon className="h-6 w-6" /></span>
            <span className="text-sm font-medium text-navy text-center">{t(it.labelKey)}</span>
          </button>
        ))}
      </div>

      {claims.length > 0 && (
        <div className="mt-10">
          <h2 className="text-lg font-semibold text-navy">{t("your_claims")}</h2>
          <div className="mt-4 space-y-3">
            {claims.map((c) => (
              <button key={c.id} data-testid={`claim-${c.id}`} onClick={() => setActive(c)}
                className="flex w-full items-center gap-4 rounded-2xl bg-white border border-slate-100 card-shadow p-4 text-left hover:shadow-md transition-shadow">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-brandblue"><ClipboardList className="h-5 w-5" /></span>
                <div className="flex-1"><p className="font-medium text-navy capitalize">{c.incident_type}</p><p className="text-xs text-slate-400">{new Date(c.created_at).toLocaleDateString()} • {c.checklist.filter((x) => x.done).length}/{c.checklist.length} {t("steps_done")}</p></div>
                <ChevronRight className="h-5 w-5 text-slate-300" />
              </button>
            ))}
          </div>
        </div>
      )}
    </AppLayout>
  );
}

function ClaimDetail({ claim, onBack, toggle, lang, t }) {
  const [denial, setDenial] = useState("");
  const [denialAns, setDenialAns] = useState("");
  const [loading, setLoading] = useState(false);

  const explainDenial = async () => {
    if (!denial.trim()) return;
    setLoading(true);
    try {
      const res = await api.post("/ai/ask", { message: `A user received this insurance claim DENIAL letter. Explain in plain language: 1) what the insurer is saying, 2) the reason/provision cited, 3) any deadline or appeal process, 4) questions to ask, 5) neutral next steps. Only use what's in the letter; if a detail isn't present, say so. Letter:\n\n${denial}`, language: lang });
      setDenialAns(res.data.answer);
    } catch { toast.error("Could not analyze"); } finally { setLoading(false); }
  };

  return (
    <AppLayout>
      <button onClick={onBack} className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-navy"><ArrowLeft className="h-4 w-4" /> {t("back_to_claims")}</button>
      <h1 className="mt-3 font-display text-3xl font-semibold text-navy capitalize">{claim.incident_type}</h1>
      {claim.copilot_summary && (
        <div className="mt-3 rounded-2xl bg-blue-50 border border-blue-100 p-4 text-sm text-navy" data-testid="copilot-summary">{claim.copilot_summary}</div>
      )}

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-3xl bg-white border border-slate-100 card-shadow p-6">
          <h2 className="text-lg font-semibold text-navy">{t("claim_checklist")}</h2>
          <p className="text-sm text-slate-400 mt-1">{t("claim_do_in_order")}</p>
          <div className="mt-4 space-y-2">
            {claim.checklist.map((item, i) => (
              <label key={i} data-testid={`checklist-${i}`} className="flex items-start gap-3 rounded-xl border border-slate-100 p-3 cursor-pointer hover:bg-slate-50">
                <input type="checkbox" checked={item.done} onChange={(e) => toggle(i, e.target.checked)} className="mt-1 h-4 w-4 accent-brandblue" />
                <span className={`text-sm ${item.done ? "text-slate-400 line-through" : "text-navy"}`}>{item.text}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-3xl bg-white border border-slate-100 card-shadow p-6">
            <h2 className="text-lg font-semibold text-navy">{t("claim_timeline")}</h2>
            <div className="mt-4 space-y-3">
              {claim.events.map((e, i) => (
                <div key={i} className="flex items-center gap-3"><CheckCircle2 className="h-4 w-4 text-covered" /><span className="text-sm text-navy">{e.label}</span><span className="ml-auto text-xs text-slate-400">{new Date(e.date).toLocaleDateString()}</span></div>
              ))}
            </div>
          </div>

          <div className="rounded-3xl bg-white border border-slate-100 card-shadow p-6">
            <h2 className="text-lg font-semibold text-navy">{t("denial_explainer")}</h2>
            <p className="text-sm text-slate-400 mt-1">{t("denial_sub")}</p>
            <textarea data-testid="denial-input" value={denial} onChange={(e) => setDenial(e.target.value)} rows={4}
              className="mt-3 w-full rounded-xl border border-slate-200 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200" placeholder={t("paste_denial")} />
            <Button data-testid="explain-denial-btn" onClick={explainDenial} disabled={loading} className="mt-3 rounded-full brand-gradient text-white">{loading ? t("analyzing") : t("explain_denial")}</Button>
            {denialAns && <div className="mt-4 rounded-xl bg-slate-50 p-4"><AIText>{denialAns}</AIText></div>}
          </div>
        </div>
      </div>

      {(claim.photograph?.length || claim.say?.length || claim.dont_say?.length || claim.draft_message) ? (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="claim-copilot">
          {claim.photograph?.length > 0 && (
            <div className="rounded-3xl bg-white border border-slate-100 card-shadow p-6">
              <h2 className="flex items-center gap-2 text-lg font-semibold text-navy"><Camera className="h-5 w-5 text-brandteal" /> {t("what_to_photograph")}</h2>
              <ul className="mt-3 space-y-2">
                {claim.photograph.map((x, i) => <li key={i} className="flex items-start gap-2 text-sm text-navy"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-brandteal shrink-0" />{x}</li>)}
              </ul>
            </div>
          )}
          <div className="space-y-6">
            {claim.say?.length > 0 && (
              <div className="rounded-3xl bg-white border border-slate-100 card-shadow p-6">
                <h2 className="flex items-center gap-2 text-lg font-semibold text-navy"><MessageCircle className="h-5 w-5 text-covered" /> {t("what_to_say")}</h2>
                <ul className="mt-3 space-y-2">
                  {claim.say.map((x, i) => <li key={i} className="flex items-start gap-2 text-sm text-navy"><CheckCircle2 className="mt-0.5 h-4 w-4 text-covered shrink-0" />{x}</li>)}
                </ul>
              </div>
            )}
            {claim.dont_say?.length > 0 && (
              <div className="rounded-3xl bg-red-50/50 border border-red-100 card-shadow p-6">
                <h2 className="flex items-center gap-2 text-lg font-semibold text-navy"><Ban className="h-5 w-5 text-red-500" /> {t("what_not_to_say")}</h2>
                <ul className="mt-3 space-y-2">
                  {claim.dont_say.map((x, i) => <li key={i} className="flex items-start gap-2 text-sm text-navy"><Ban className="mt-0.5 h-4 w-4 text-red-400 shrink-0" />{x}</li>)}
                </ul>
              </div>
            )}
          </div>
          {claim.draft_message && (
            <div className="lg:col-span-2 rounded-3xl bg-white border border-slate-100 card-shadow p-6">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-navy">{t("draft_message")}</h2>
                <Button data-testid="copy-draft" variant="outline" className="rounded-full h-9"
                  onClick={() => { navigator.clipboard?.writeText(claim.draft_message); toast.success(t("copied")); }}>
                  <Copy className="h-4 w-4 mr-1" />{t("copy")}
                </Button>
              </div>
              <pre className="mt-3 whitespace-pre-wrap rounded-xl bg-slate-50 p-4 text-sm text-navy font-sans leading-relaxed">{claim.draft_message}</pre>
            </div>
          )}
        </div>
      ) : null}
    </AppLayout>
  );
}
